import type { PlayerStatsResponse, LeagueBattingStatRow, LeaguePitchingStatRow, LeagueFieldingStatRow } from '@/api/generated/models'
import type { StatLine, ExtendedBattingLine, ExtendedPitchingLine, FieldingLine } from '../types'
import { mapBattingLine, mapPitchingLine } from './statLines'

/** Never coerce null to 0 — null means "not applicable / not recorded". */
function n(v: number | null | undefined): number | undefined {
  return v == null ? undefined : v
}

function teamLabel(city?: string | null, name?: string | null): string | undefined {
  const parts = [city, name].filter(Boolean)
  return parts.length ? parts.join(' ') : undefined
}

export function mapStats(api: PlayerStatsResponse): { seasonStats: StatLine[] } {
  const isPitcher = api.isPitcher ?? false
  const rows: StatLine[] = []

  if (isPitcher) {
    for (const row of api.pitchingStandard ?? []) {
      rows.push(mapPitchingLine(row))
    }
    if (api.pitchingStandardCareer) {
      rows.push(mapPitchingLine(api.pitchingStandardCareer, 'Career'))
    }
  } else {
    for (const row of api.battingStandard ?? []) {
      rows.push(mapBattingLine(row))
    }
    if (api.battingStandardCareer) {
      rows.push(mapBattingLine(api.battingStandardCareer, 'Career'))
    }
  }

  return { seasonStats: rows }
}

/** Map a single extended-batting row (real field names from /Stats?part=). */
export function mapExtendedBattingLine(r: LeagueBattingStatRow, label?: string): ExtendedBattingLine {
  return {
    label: label ?? (r.year != null ? String(r.year) : undefined),
    teamId: r.teamId,
    team: teamLabel(r.city, r.name),
    pa: n(r.spa),
    tb: n(r.stb),
    // API sends gbfb as a fraction (e.g. 0.53); the card formats it as a whole `${v}%`,
    // so scale to percent and round (avoids float noise like 56.000000000000007%).
    gbfb: r.sgbfb != null ? Math.round(r.sgbfb * 100) : undefined,
    sf: n(r.ssf),
    hbp: n(r.shbp),
    gdp: n(r.sgdp),
    roe: n(r.sroe),
    xb: n(r.sxb),
    bro: n(r.sbro),
    sa: n(r.ssa),
    rc: n(r.src),
    lwts: n(r.slwts),
    abhr: n(r.sabhr),
    tbpa: n(r.stbpa),
  }
}

/** Map a single extended-pitching row (real field names from /Stats?part=). */
export function mapExtendedPitchingLine(r: LeaguePitchingStatRow, label?: string): ExtendedPitchingLine {
  return {
    label: label ?? (r.year != null ? String(r.year) : undefined),
    teamId: r.teamId,
    team: teamLabel(r.city, r.name),
    so9: n(r.sso9),
    bb9: n(r.sbb9),
    sobb: n(r.ssobb),
    go: n(r.sgo),
    fo: n(r.sfo),
    ffo: n(r.sffo),
    hr: n(r.shr),
    ir: n(r.sir),
    irs: n(r.sirs),
    oavg: n(r.soavg),
    oobp: n(r.soobp),
    oslg: n(r.soslg),
    oops: n(r.soops),
    lwts: n(r.slwts),
    babip: n(r.sbabip),
  }
}

/** Map a single fielding row. pos is the server-sent label (dbo.PlayerGetSeasonStats /
 *  PlayerGetPlayoffStats join dbo.positions); the client does not decode posn. */
export function mapFieldingLine(r: LeagueFieldingStatRow, year?: string): FieldingLine {
  return {
    year: year ?? (r.year != null ? String(r.year) : undefined),
    teamId: r.teamId,
    team: teamLabel(r.city, r.name),
    pos: r.posnShort ?? '',
    g: n(r.sg),
    po: n(r.spo),
    a: n(r.sa),
    e: n(r.se),
    tc: n(r.stc),
    dp: n(r.sdp),
    sb: n(r.ssb),
    cs: n(r.scs),
    // API sends scspct as a fraction (e.g. 0.297, verified on test API); the card formats it `${v.toFixed(1)}%`, so scale to percent.
    csPct: r.scspct != null ? r.scspct * 100 : undefined,
    pct: n(r.spct),
    rng: n(r.srng),
  }
}

/** Extended batting rows (season-by-season + Career). */
export function mapExtendedBatting(api: PlayerStatsResponse): ExtendedBattingLine[] {
  const rows: ExtendedBattingLine[] = []
  for (const row of api.battingExtended ?? []) {
    rows.push(mapExtendedBattingLine(row))
  }
  if (api.battingExtendedCareer) {
    rows.push(mapExtendedBattingLine(api.battingExtendedCareer, 'Career'))
  }
  return rows
}

/** Extended pitching rows (season-by-season + Career). */
export function mapExtendedPitching(api: PlayerStatsResponse): ExtendedPitchingLine[] {
  const rows: ExtendedPitchingLine[] = []
  for (const row of api.pitchingExtended ?? []) {
    rows.push(mapExtendedPitchingLine(row))
  }
  if (api.pitchingExtendedCareer) {
    rows.push(mapExtendedPitchingLine(api.pitchingExtendedCareer, 'Career'))
  }
  return rows
}

/** Fielding rows (per season+position) plus per-position Career rows. */
export function mapFielding(api: PlayerStatsResponse): FieldingLine[] {
  const rows: FieldingLine[] = []
  for (const row of api.fielding ?? []) {
    rows.push(mapFieldingLine(row))
  }
  for (const row of api.fieldingCareer ?? []) {
    rows.push(mapFieldingLine(row, 'Career'))
  }
  return rows
}
